import type { MyWork } from './my-work.types'
import { prTarget } from './my-work.utils'

/** The pages that offer Only mine; each remembers its own choice. */
export type OnlyMinePage = 'release' | 'qa-queue' | 'fleet-runs' | 'claude-needs-attention' | 'claude-oversight'

export const onlyMineStorageKey = (page: OnlyMinePage) => `adx-claude-only-mine:${page}`

/**
 * Waiting on people became Needs attention, so what this browser kept under the old page name moves to the new one,
 * once: the Only mine choice and the saved views. An entry already kept under the new name wins, and the old one is
 * removed either way. Storage that is blocked just leaves the page starting fresh.
 */
export function migrateWaitingStorage(): void {
  const moves: [string, string][] = [
    [onlyMineStorageKey('claude-waiting' as OnlyMinePage), onlyMineStorageKey('claude-needs-attention')],
    ['adx.saved-views.claude-waiting', 'adx.saved-views.claude-needs-attention']
  ]
  try {
    for (const [from, to] of moves) {
      const old = localStorage.getItem(from)
      if (old === null) continue
      if (localStorage.getItem(to) === null) localStorage.setItem(to, old)
      localStorage.removeItem(from)
    }
  } catch {
    // Blocked storage has nothing to carry over.
  }
}

/** The page's remembered Only mine choice on this browser; null when none, or when storage is blocked. */
export function readOnlyMineChoice(page: OnlyMinePage): string | null {
  try {
    return localStorage.getItem(onlyMineStorageKey(page))
  } catch {
    return null
  }
}

/**
 * Whether a page opens filtered: `?mine=1` or `?mine=0` in the link wins, so a shared link shows what its
 * sender saw; with neither, the page's last choice on this browser; with no choice saved, everything.
 */
export function initialOnlyMine(queryValue: unknown, stored: string | null): boolean {
  if (queryValue === '1') return true
  if (queryValue === '0') return false
  return stored === '1'
}

/** What is the signed-in person's, from their own My work: the same identity My work uses. */
export interface MineSets {
  email: string
  ticketKeys: Set<string>
  /** `<repo>#<id>` of the PRs they opened and the ones waiting on their review. */
  prKeys: Set<string>
  /** `<key>#<anchor>` of the decisions asked of them. */
  decisionKeys: Set<string>
  decisionTickets: Set<string>
}

export function mineSetsOf(work: MyWork): MineSets {
  const prs = [...work.myPrs, ...work.reviewRequested, ...work.tickets.flatMap(ticket => ticket.prs)]
  return {
    email: work.me.email.toLowerCase(),
    ticketKeys: new Set(work.tickets.map(ticket => ticket.key)),
    prKeys: new Set(prs.map(pr => `${pr.repo}#${pr.id}`)),
    decisionKeys: new Set(work.decisions.map(decision => `${decision.key}#${decision.anchor}`)),
    decisionTickets: new Set(work.decisions.map(decision => decision.key))
  }
}

/** A release ticket is mine when it is assigned to me, carries a PR I opened or review, or asks me a decision. */
export function isMyReleaseTicket(
  ticket: { key: string; mine: boolean; prs: { repo: string; id: number }[] | null },
  mine: MineSets
): boolean {
  return (
    ticket.mine ||
    mine.ticketKeys.has(ticket.key) ||
    mine.decisionTickets.has(ticket.key) ||
    (ticket.prs ?? []).some(pr => mine.prKeys.has(`${pr.repo}#${pr.id}`))
  )
}

/** A run is mine when it ran on my laptop, I started it, or it worked on one of my tickets or PRs. */
export function isMyRun(
  run: { runnerOwner?: string; trigger?: string; item?: string; tickets?: string[]; pullRequests?: string[] },
  mine: MineSets
): boolean {
  const startedBy = /^(adx|mcp):(.+)$/.exec(run.trigger ?? '')?.[2]?.toLowerCase()
  const touched = [...(run.tickets ?? []), ...(run.pullRequests ?? []), ...(run.item ? [run.item] : [])]
  return (
    (Boolean(mine.email) && (run.runnerOwner?.toLowerCase() === mine.email || startedBy === mine.email)) ||
    touched.some(target => mine.ticketKeys.has(target) || mine.prKeys.has(target))
  )
}

/** A decision on the team list is mine when My work lists it as asked of me. */
export const isMyDecision = (decision: { key: string; anchor: string }, mine: MineSets): boolean =>
  mine.decisionKeys.has(`${decision.key}#${decision.anchor}`)

/** An Oversight card is mine when its ticket is one of mine, or one of its PRs is mine or waits on my review. */
export const isMyCard = (card: { key: string; prs: { repo: string; id: number }[] }, mine: MineSets): boolean =>
  mine.ticketKeys.has(card.key) || card.prs.some(pr => mine.prKeys.has(prTarget(pr)))

/** A ready PR is mine when I own it, or it waits on my review. */
export const isMyReadyPr = (owner: string, pr: { repo: string; id: number }, mine: MineSets): boolean =>
  isOwnedBy(owner, mine.email) || mine.prKeys.has(prTarget(pr))

/** The same person, by email, case aside; an empty email (or the test reader's hidden name) is nobody. */
export const isOwnedBy = (owner: string, email: string | undefined): boolean =>
  Boolean(email && owner.toLowerCase() === email.toLowerCase())

/** Shown when Only mine is on and My work, which says what is mine, could not be read. */
export const ONLY_MINE_UNREADABLE =
  'Only mine works out what is yours from My work, which could not be read just now. Turn Only mine off to see everything.'
